import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import AppShell from '../components/AppShell';
import api from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Help Center — static FAQ articles.
 * Reference: /app/reference/14-help-center.html
 *
 * Route: /help  (linked from Preferences → "Help center")
 *
 * All content is static (no backend). Client-side filtering by:
 * - category chip
 * - free-text search across question + answer
 */

const ARTICLES = [
  // ---------- Getting Started (NEW — user-requested) ----------
  {
    id: 'gs-deposit',
    category: 'Getting Started',
    q: 'How do I deposit?',
    a: "Tap your avatar → Wallet → Deposit. Pick 'Pay with crypto' and either let the checkout show every supported currency or pre-select one (BTC, ETH, USDC, SOL, and more). Minimum deposit is $10, maximum is $50,000 per invoice, and 1 pt = $1 USD. Once the on-chain confirmation clears (usually 2–30 minutes depending on the network), Points land in your Wallet within seconds.",
  },
  {
    id: 'gs-withdraw',
    category: 'Getting Started',
    q: 'How do I withdraw?',
    a: "Wallet → Withdraw. Minimum is $10, maximum is $2,500 per request. Paste the crypto address to send to (recent addresses are saved as one-click chips), pick the network, and confirm. Requests placed between 10:00 AM and 7:00 PM UTC usually settle within the hour; outside that window they process the next working day, and we cap the total wait at 16 hours. Why not instant yet? Every withdrawal is reviewed manually while Clanster is young to protect against double-spend attempts and compromised accounts — on-chain automation is on the roadmap.",
  },
  {
    id: 'gs-make-profits',
    category: 'Getting Started',
    q: 'How can I make profits?',
    a: "Two levers: (1) Trading — buy Bits early when a Clan is small and sell later once more fans have bought in and pushed the price up. The bonding curve is transparent, so early buyers always pay the cheapest price. (2) Referring — every fan who signs up with your link earns you 1% of every trade they ever make, forever. Launching your own Clan adds a third stream: 5% royalty on every trade + 100% of perk revenue. Nothing is guaranteed — Bit prices can fall if fans sell, and you should never spend more than you can afford to lose.",
  },
  {
    id: 'gs-find-best-clans',
    category: 'Getting Started',
    q: 'How do I find the best Clans?',
    a: "Three surfaces to hunt: (a) Home feed sorts creators by momentum, trending, and 'filling fast' filters — great for discovery. (b) Trade tab shows a live trending marquee and a searchable Clan grid with real-time price charts — perfect when a launch is popping. (c) Active Clans lists creators you already hold, so you can rebalance quickly. Look for the momentum chip on cards (green +% this week) and the LIVE dot — those signal creators actively growing. Do your own research: check their socials, watch a stream, read their pinned post before buying.",
  },
  {
    id: 'gs-clan-optional',
    category: 'Getting Started',
    q: 'Is it mandatory to create a Clan?',
    a: "Absolutely not. You can enjoy the entire platform as a fan — buy Bits, join Clan feeds, complete Bounties, redeem Perks, refer friends. Creating a Clan is only for people who want to monetize their community and open up a new income stream. If you're not sure, skip it for now and try the fan experience first.",
  },
  {
    id: 'gs-small-audience',
    category: 'Getting Started',
    q: "I don't have a big audience — should I still create a Clan?",
    a: "Yes, especially now. Clanster's bonding curve rewards early followers who back you before the crowd — a Clan of 20 committed fans can generate meaningful royalties. Pick the Open House community style (1-Bit floor) and the smallest opening Bit price (0.21 pts) so the entry bar is friction-free. Bring your existing friends and DMs in first — even 5–10 loyal fans is a real starting point. Volume grows from there.",
  },
  {
    id: 'gs-minimum-amount',
    category: 'Getting Started',
    q: 'What is the minimum amount to get started?',
    a: "As a fan: the smallest deposit is $10, which converts to 10 pts. That's usually enough to buy several Bits in a fresh Clan and unlock its private feed. As a creator: no upfront cost to design your Clan — you only pay a small mint fee (10 pts) when you go live, and the first 100 creators globally get it free forever. Every fee (trading, perk creation, featured slots) is denominated in Points, never in real currency.",
  },
  {
    id: 'gs-no-crypto',
    category: 'Getting Started',
    q: "I don't have crypto — can I still use the App?",
    a: "You can sign up, browse Clans, follow creators, read pinned posts, and complete free Bounties without ever touching crypto. To actually buy Bits or redeem Perks, you'll need to deposit — and that requires crypto right now (BTC, ETH, USDC, SOL and more via our payments partner). Card and Apple Pay on-ramps are on the roadmap. If you're new to crypto, the easiest path is a mainstream exchange (Coinbase / Binance / Kraken) → buy USDC → send to your Clanster deposit address.",
  },

  // ---------- Trading ----------
  {
    id: 'what-is-bit',
    category: 'Trading',
    q: 'What is a Bit, exactly?',
    a: "A Bit is a tradable share in a creator's Clan. Its price moves on a transparent square-root bonding curve — every buy nudges the price up smoothly, every sell nudges it back down. There's no fixed supply and no sold-out state — the curve keeps growing (with a decelerating slope) as more fans join. Holding at least the Clan's minimum Bits unlocks the private Feed, Live, Chat and Perks.",
  },
  {
    id: 'linear-curve',
    category: 'Trading',
    q: 'How is the Bit price calculated?',
    a: "It's a square-root formula: price of Bit #n = multiplier × √n. So if the multiplier is 0.85 pts, the 1st Bit costs 0.85 pts, the 100th costs 8.5 pts, the 10,000th costs 85 pts. Growth is fast at first (rewarding early fans) and decelerates as the Clan gets bigger (so latecomers still get a fair price). Every buy mints the next Bit on the curve; every sell burns the most-recent one. No hidden slippage, no order book — the math is fully deterministic.",
  },
  {
    id: 'fee-split',
    category: 'Trading',
    q: 'How are trading fees split?',
    a: 'Every trade carries an 11% fee: 5% goes to the creator as royalty (paid instantly to their Wallet), 5% to Clanster, and 1% to whoever referred the buyer. Buys apply the fee on top of the subtotal; sells deduct it from the payout.',
  },
  {
    id: 'cost-basis',
    category: 'Trading',
    q: 'How is my P&L calculated?',
    a: 'We track a weighted-average cost basis per Clan. When you sell, we reduce your cost basis proportionally to the qty sold — the P&L on your Wallet is the realised gain from previous sells plus the unrealised gain based on the current market price. Expand any holding row to see the exact breakdown.',
  },
  {
    id: 'scenarios',
    category: 'Trading',
    q: 'What is the "What could happen?" panel on the Buy/Sell screen?',
    a: "It's a plain-English forecast: given how many Bits you're about to buy or sell, we run the sqrt curve at three future milestones — 1K, 10K and 100K total Bits minted — and show what your position would be worth if you sold at each stage. It's an exact math projection, not a guess. On legacy linear-curve Clans (rare, pre-migration) it shows 25% / 60% / 100% fill instead.",
  },
  {
    id: 'buy-max',
    category: 'Trading',
    q: 'What does "Buy Max" actually buy?',
    a: "It buys as many Bits as your Points balance can afford, capped by the Clan's dynamic per-wallet whale limit (3% of minted supply, floor of 100 Bits during early launches). If your balance falls in-between two Bits, the leftover pts stay in your Wallet — no error, no forced rounding.",
  },
  {
    id: 'sold-out',
    category: 'Trading',
    q: 'Do Clans ever sell out?',
    a: "No. Clanster's sqrt bonding curve has no supply cap — it just keeps rising with a decelerating slope as more fans buy in. That means there's always a next Bit available, and creators never hit an artificial ceiling on their earnings. A few legacy linear-curve Clans still exist from before the migration; those keep the old cap-based behaviour where holders must sell before new fans can buy.",
  },
  {
    id: 'fair-batching',
    category: 'Trading',
    q: 'Is Clanster fair against bots and HFT?',
    a: "Yes — by design. Every trade is timestamped to the microsecond and grouped into 500 ms batch windows. Trades that arrive in the same window are randomly shuffled before execution, so a fan on 4G has the exact same shot as a bot with a co-located server. Look for the 🎲 badge on any Clan's trade sheet, and you can download a cryptographically-signed replay of any launch from the creator's Manage Clan page to prove the fairness math held.",
  },
  {
    id: 'slippage',
    category: 'Trading',
    q: 'What is the slippage indicator on the trade panel?',
    a: "It shows the % gap between the current spot price and the average price you'll actually pay for your chosen qty (bigger buys move deeper along the curve, so bigger slippage). If the price moves past your ceiling between click and settlement, Clanster automatically shrinks your qty up to 4 times to stay within your Auto / +2% / +5% / +10% slippage tolerance — no forced overpay.",
  },

  // ---------- Clans ----------
  {
    id: 'start-clan',
    category: 'Clans',
    q: 'How do I start my own Clan?',
    a: "Tap 'Create Clan' from your avatar menu. The wizard asks just 2 questions: (1) Opening Bit price — sets how fast your earnings grow with every trade; and (2) Community Style — Open House / Real Fans / Inner Circle, which sets the minimum Bits a fan needs to enter your private feed. Add up to 7 perks, review, and hit Mint. Your Genesis Bit is auto-issued to you at 0 pts. A small mint fee (default 10 pts) is deducted from your balance — the first 100 creators globally get it free forever.",
  },
  {
    id: 'supply-cap',
    category: 'Clans',
    q: 'Is there a supply cap on my Clan?',
    a: "No — Clanster uses a sqrt bonding curve, which means your Clan has UNLIMITED supply. The price rises smoothly as fans buy in, then decelerates so latecomers still get a fair entry. There's no sold-out state, no artificial ceiling on your earnings, and no need to guess your audience size at mint. Opening Bit price and Community Style are locked at mint so holders can trust the math; everything else (perks, cover, bio, min-Bits floor) is editable.",
  },
  {
    id: 'whale-cap',
    category: 'Clans',
    q: 'What is the per-wallet whale cap?',
    a: "It's the maximum Bits a single wallet can hold in one Clan. This stops one person from cornering supply and blocking your community. The cap is DYNAMIC — 3% of the current minted supply, with a floor of 100 Bits for early-launch participation. As your Clan grows, the whale cap grows in absolute terms, so long-time supporters never hit a ceiling.",
  },
  {
    id: 'min-bits-editable',
    category: 'Clans',
    q: 'Can I change the minimum Bits after launching?',
    a: 'Yes — creators can raise or lower the min-Bits-to-join at any time from Manage Clan → Settings. Opening Bit price and Community Style stay locked. Lowering the floor never dispossesses anyone; raising it may lock some fans out of Feed/Live/Chat until they buy more Bits.',
  },
  {
    id: 'below-min-bits',
    category: 'Clans',
    q: "What happens if I fall below a Clan's minimum Bits?",
    a: "You automatically lose access to that Clan's private Feed, Live, Chat and Perks purchases until you hold the minimum again. Your Bits themselves are never affected — you can still trade them normally.",
  },
  {
    id: 'exclusive-posts',
    category: 'Clans',
    q: 'Can I see posts before joining a Clan?',
    a: "No — Clan Feed, Live and Chat are gated behind the min-Bits requirement. When the creator is currently live you'll see a 'LIVE NOW' teaser card with the stream title, tags and cover so you can decide if it's worth buying in.",
  },
  {
    id: 'featured',
    category: 'Clans',
    q: 'What is Featured, and how do I get featured?',
    a: 'Featured slots put your Clan at the top of the Home feed. Buy one from Manage Clan → Feature: 25 pts for 1 day, 125 pts for a week, 400 pts for a month. Slots are non-refundable once the window starts.',
  },
  {
    id: 'growth-chip',
    category: 'Clans',
    q: "What is the momentum chip on some creators' cards?",
    a: 'When a creator connects their YouTube (with TikTok + Instagram coming soon), we poll their subscriber/follower count every 6 hours. If they gain traction we surface a "+12% this week" chip on their card and rank them higher on the Trending Clans list. Connect yours from Manage Clan → Growth.',
  },

  // ---------- Perks ----------
  {
    id: 'what-are-perks',
    category: 'Perks',
    q: 'What is the Perks Marketplace?',
    a: "Perks are extras a creator offers to fans holding at least a certain number of Bits — 1-on-1 calls, shoutouts, presets, custom art, whatever. Fans pay in Points to redeem, and the creator receives 100% of the Points (no platform cut on perk redemptions).",
  },
  {
    id: 'create-perk',
    category: 'Perks',
    q: 'How do I list a perk?',
    a: "From Manage Clan → Perks → Add Perk. Set a title, description, Points price, required Bits to unlock, and stock qty. Publishing costs a small creation fee (default 10 pts) — but the first 100 creators globally get it free forever as 'Founding Perk Creators'.",
  },
  {
    id: 'redeem-perk',
    category: 'Perks',
    q: 'How do I redeem a perk I bought?',
    a: "Open the Clan, tap Perks, hit Redeem. The Points cost is debited immediately and the creator is notified. Fulfilment happens off-app via DM or the delivery channel the creator specifies in the perk description. Any refund is at the creator's discretion.",
  },

  // ---------- Bounties ----------
  {
    id: 'what-are-bounties',
    category: 'Bounties',
    q: 'What are Bounties?',
    a: "Bounties are creator-funded quests for their community — 'record a TikTok using this sound', 'reply to this X post with a screenshot', 'design a poster'. The creator locks a reward pool in Points; fans submit proof; the creator picks winners and Points are paid out automatically.",
  },
  {
    id: 'bounty-proof',
    category: 'Bounties',
    q: 'What kind of proof can I submit?',
    a: "It depends on what the creator picked when arming the bounty: plain text, a URL, or an image screenshot upload. Follow the instructions on the bounty card exactly — creators reserve the right to reject vague or off-topic submissions.",
  },
  {
    id: 'bounty-fees',
    category: 'Bounties',
    q: 'Are there fees on Bounty payouts?',
    a: "No. The full reward pool the creator locks is what gets paid to winners — no platform cut. If a bounty ends with unclaimed slots, the leftover Points are refunded to the creator's balance.",
  },

  // ---------- Airdrops ----------
  {
    id: 'what-are-airdrops',
    category: 'Airdrops',
    q: 'What are Milestone Airdrops?',
    a: "Creators can pre-arm a Points reward that automatically pays out to their top holders once a milestone is hit — e.g. 'when the Clan reaches 500 Bits minted, split 200 pts across the top 10 holders'. Fully transparent: the armed milestone shows up on the Clan page before it triggers.",
  },
  {
    id: 'airdrop-eligibility',
    category: 'Airdrops',
    q: 'How do I qualify for an airdrop?',
    a: "Just hold Bits — the more you hold, the higher you rank on the top-holder leaderboard when the milestone fires. You'll see a notification the moment Points land in your Wallet, and the event is logged on the Clan's public airdrop history.",
  },

  // ---------- Payments ----------
  {
    id: 'points-vs-crypto',
    category: 'Payments',
    q: "What's the difference between Points and crypto?",
    a: 'Points (pts) are your in-app balance and the only thing used to buy/sell Bits, redeem perks, arm bounties, or pay for Featured slots. Deposits convert your crypto to pts at 1 pt = $1 USD. Withdrawals do the reverse. Prices, fees and rewards are all denominated in pts.',
  },
  {
    id: 'deposit-min',
    category: 'Payments',
    q: 'What are the deposit limits?',
    a: 'Minimum deposit is $10 and maximum is $50,000 per invoice. Pick "Pay with crypto" on the Wallet page — you can either let NowPayments show every supported currency at checkout or pre-select one (BTC, ETH, USDC, SOL and more).',
  },
  {
    id: 'deposit-time',
    category: 'Payments',
    q: 'How long does a deposit take to arrive?',
    a: 'Once the on-chain confirmation clears (usually 2–30 minutes depending on the network), Points are credited to your Wallet within seconds. If it has been over an hour with no update, contact us with the deposit invoice ID.',
  },
  {
    id: 'withdraw',
    category: 'Payments',
    q: 'How do I withdraw my Points?',
    a: 'Wallet → Withdraw. Minimum withdrawal is $10 and maximum is $2,500 per request. Withdrawals are manually reviewed for security and typically settle within 24 hours to the crypto address you supply. Recent addresses are saved as one-click chips for the next time you cash out.',
  },
  {
    id: 'withdrawal-timing',
    category: 'Payments',
    q: 'How long do withdrawals take to process?',
    a: "Requests placed between 10:00 AM and 7:00 PM UTC are usually settled within the hour. Requests placed outside that window are processed the next working day — in all cases we cap the wait at 16 hours from submission. The reason it isn't instant yet: Clanster is new and every withdrawal is reviewed manually to protect the platform against abuse (double-spend attempts, compromised accounts, sanctioned addresses, etc.). Once our on-chain automation ships, withdrawals will move to near-instant settlement.",
  },
  {
    id: 'creator-earnings',
    category: 'Payments',
    q: 'When and how do creators get paid?',
    a: 'Creator royalties (5% of every trade) land in the creator\'s Points Wallet instantly at the moment of trade — no vesting, no batching. Perk redemptions credit 100% of the Points to the creator immediately as well. From there they can withdraw to crypto like any other user.',
  },

  // ---------- Account ----------
  {
    id: 'signup-referral',
    category: 'Account',
    q: 'How do referrals work?',
    a: "When someone signs up with your referral code, you earn 1% of every trade fee they generate — forever. Tap your avatar → 'Refer friends' to grab your code, share your invite link, and see everyone who joined with your code along with how much you've earned from each.",
  },
  {
    id: 'change-password',
    category: 'Account',
    q: 'How do I change my password?',
    a: "Preferences → Profile → 'Change password'. You'll be asked for your current password before setting a new one. If you've forgotten it, use the 'Forgot password?' link on the Login screen — a reset email will be sent to your registered address.",
  },
  {
    id: 'delete-account',
    category: 'Account',
    q: 'Can I delete my account?',
    a: "Yes — email support@clanster.xyz from your registered address. We'll walk you through the checklist (withdraw any remaining Points, sell down open positions, transfer clan ownership if applicable). Account deletion is irreversible and any residual Points balance is forfeited.",
  },

  // ---------- Rewards ----------
  {
    id: 'rewards',
    category: 'Rewards',
    q: 'How do reward points work?',
    a: "Reward Points sit alongside your trading Wallet and are earned through platform activity — daily check-ins, referrals, joining Clans, watching live streams. Reward streams are toggled globally by the admin, so which ones are live can change over time. Check Wallet → Rewards for what's active right now.",
  },
];

const CATEGORIES = ['All', 'Getting Started', 'Account', 'Trading', 'Clans', 'Perks', 'Bounties', 'Airdrops', 'Payments', 'Rewards'];

function ReplayTourButton() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  const run = async (path, redirectTo) => {
    setBusy(path);
    try {
      await api.post(path);
      await refreshMe();
      navigate(redirectTo);
    } catch (_) { /* non-fatal */ }
    finally { setBusy(null); }
  };
  return (
    <div className="help-replay-cluster" data-testid="help-replay-cluster">
      <button
        type="button"
        className="help-replay-tour"
        onClick={() => run('/auth/tour-reset', '/home')}
        disabled={busy !== null}
        data-testid="help-replay-tour-btn"
        title={t('helpCentre.replayIntroTitle')}
      >
        {busy === '/auth/tour-reset' ? '…' : t('helpCentre.replayIntro')}
      </button>
      <button
        type="button"
        className="help-replay-tour"
        onClick={() => run('/auth/wizard-tour-reset', '/create-clan')}
        disabled={busy !== null}
        data-testid="help-replay-wizard-tour-btn"
        title={t('helpCentre.replayCreateClanTitle')}
      >
        {busy === '/auth/wizard-tour-reset' ? '…' : t('helpCentre.replayCreateClan')}
      </button>
      <button
        type="button"
        className="help-replay-tour"
        onClick={() => run('/auth/creator-tour-reset', '/manage-clan')}
        disabled={busy !== null}
        data-testid="help-replay-creator-tour-btn"
        title={t('helpCentre.replayManageClanTitle')}
      >
        {busy === '/auth/creator-tour-reset' ? '…' : t('helpCentre.replayManageClan')}
      </button>
    </div>
  );
}


export default function HelpCenter() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [category, setCategory] = useState('All');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState(null);
  // Search-bar autocomplete state. Suggestions render only when the
  // input is focused AND the query is 2+ chars — keeps the panel clean
  // when users are just glancing at the page.
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ARTICLES.filter((a) => {
      if (category !== 'All' && a.category !== category) return false;
      if (!q) return true;
      return a.q.toLowerCase().includes(q) || a.a.toLowerCase().includes(q);
    });
  }, [category, query]);

  // Title-only matches across ALL categories — powers the auto-suggest
  // dropdown so a user typing "deposit" jumps straight to the Getting
  // Started article regardless of the currently-selected chip. Capped at
  // 6 rows so the panel stays lightweight on mobile.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    // Rank: prefix-match on the question first, then substring, then
    // substring on the answer. Stable ordering by original DB position.
    const scored = [];
    ARTICLES.forEach((a, idx) => {
      const ql = a.q.toLowerCase();
      const al = a.a.toLowerCase();
      let score = 0;
      if (ql.startsWith(q)) score = 3;
      else if (ql.includes(q)) score = 2;
      else if (al.includes(q)) score = 1;
      if (score > 0) scored.push({ a, score, idx });
    });
    scored.sort((x, y) => (y.score - x.score) || (x.idx - y.idx));
    return scored.slice(0, 6).map((s) => s.a);
  }, [query]);

  const openArticle = (a) => {
    // Ensure the article is visible even if the user was on a different
    // category chip — jumping to 'All' guarantees it renders in the list.
    setCategory('All');
    setQuery('');
    setSearchFocused(false);
    setActiveSuggestion(-1);
    setOpenId(a.id);
    // Blur the input so mobile keyboards close + the dropdown definitively
    // unmounts (some Android WebViews keep focus on preventDefault'd inputs).
    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
      document.activeElement.blur();
    }
    // Scroll it into view on the next frame after the collapsed row expands.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="help-item-${a.id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const onSearchKeyDown = (e) => {
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = activeSuggestion >= 0 ? suggestions[activeSuggestion] : suggestions[0];
      if (pick) openArticle(pick);
    } else if (e.key === 'Escape') {
      setSearchFocused(false);
      setActiveSuggestion(-1);
    }
  };

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <AppShell activeTab="wallet">
      <div className="help-wrap" data-testid="help-page">
        <div className="help-topbar">
          <button
            className="help-back"
            onClick={() => navigate(-1)}
            aria-label={t('helpCentre.backAria')}
            data-testid="help-back-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
              <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="help-title" data-testid="help-title">{t('helpCentre.title')}</h1>
          <ReplayTourButton />
        </div>

        {/* Public illustrated walkthroughs — dedicated tile row, pinned
            above the FAQ so anyone landing on Help Center sees them
            first. Guides open in a new tab so the help session isn't
            lost. */}
        <div className="help-guides-row" data-testid="help-guides-row">
          <a
            href="/guides/creator-prep"
            target="_blank"
            rel="noopener noreferrer"
            className="help-guide-tile help-guide-creator"
            data-testid="help-guide-tile-creator"
          >
            <div className="help-guide-emoji" aria-hidden="true">🚀</div>
            <div className="help-guide-body">
              <div className="help-guide-eyebrow">For creators</div>
              <div className="help-guide-title">Creator Launch Playbook</div>
              <div className="help-guide-sub">6-step illustrated walkthrough: onboarding → referral link → priming your audience → launch day → compounding royalties.</div>
              <div className="help-guide-cta">Read the guide →</div>
            </div>
          </a>
          <a
            href="/guides/audience-prep"
            target="_blank"
            rel="noopener noreferrer"
            className="help-guide-tile help-guide-audience"
            data-testid="help-guide-tile-audience"
          >
            <div className="help-guide-emoji" aria-hidden="true">🎉</div>
            <div className="help-guide-body">
              <div className="help-guide-eyebrow">For fans</div>
              <div className="help-guide-title">Fan Trading Playbook</div>
              <div className="help-guide-sub">Everything a new fan needs: signup → Wallet → buying + selling Bits → hunting Perks &amp; Bounties → the aspirational math on early-buyer upside.</div>
              <div className="help-guide-cta">Read the guide →</div>
            </div>
          </a>
        </div>

        <div className="help-search" data-testid="help-search-bar">
          <svg viewBox="0 0 24 24" fill="none" width="17" height="17" style={{ opacity: 0.55 }}>
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
            <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder={t('helpCentre.searchPlaceholder')}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveSuggestion(-1); }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => { setTimeout(() => setSearchFocused(false), 140); }}
            onKeyDown={onSearchKeyDown}
            data-testid="help-search-input"
            autoComplete="off"
          />
          {searchFocused && suggestions.length > 0 && (
            <ul className="help-suggestions" data-testid="help-suggestions" role="listbox">
              {suggestions.map((a, i) => (
                <li
                  key={a.id}
                  role="option"
                  aria-selected={i === activeSuggestion}
                  className={`help-suggestion${i === activeSuggestion ? ' active' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); openArticle(a); }}
                  onMouseEnter={() => setActiveSuggestion(i)}
                  data-testid={`help-suggestion-${a.id}`}
                >
                  <span className="help-suggestion-q">{a.q}</span>
                  <span className="help-suggestion-cat">{a.category}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="help-cats" data-testid="help-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className={`help-cat${category === c ? ' selected' : ''}`}
              onClick={() => setCategory(c)}
              data-testid={`help-cat-${c.toLowerCase()}`}
            >
              {c === 'All' ? t('helpCentre.categoryAll') : t(`helpCentre.categoryLabels.${c}`, c)}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="help-empty" data-testid="help-empty">
            <div className="help-empty-emoji">🔎</div>
            <div className="help-empty-title">{t('helpCentre.noResultsTitle')}</div>
            <p className="help-empty-sub">
              {t('helpCentre.noResultsSub')}
            </p>
          </div>
        ) : (
          <div className="help-list" data-testid="help-list">
            {filtered.map((a) => {
              const isOpen = openId === a.id;
              return (
                <div
                  key={a.id}
                  className={`help-item${isOpen ? ' open' : ''}`}
                  data-testid={`help-item-${a.id}`}
                >
                  <button
                    type="button"
                    className="help-q"
                    onClick={() => toggle(a.id)}
                    aria-expanded={isOpen}
                    data-testid={`help-q-${a.id}`}
                  >
                    <span className="help-q-text">{a.q}</span>
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      width="16"
                      height="16"
                      style={{
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0,
                        opacity: 0.65,
                      }}
                    >
                      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="help-a" data-testid={`help-a-${a.id}`}>
                      {a.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="help-contact" data-testid="help-contact">
          <div className="help-contact-title">{t('helpCentre.stillStuck')}</div>
          <p className="help-contact-sub">
            <Trans
              i18nKey="helpCentre.contactBody"
              components={{ 1: <a href="mailto:support@clanster.xyz" /> }}
            />
          </p>
        </div>
      </div>
    </AppShell>
  );
}
